import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';
import { BASE_URL } from '../../config';
import authStorage from '../../utils/authStorage';
import { refreshFeatureFlags } from '../../utils/featureFlags';
import CorporateBilling from './corporateBilling';

const BRAND = '#E53E14';

/**
 * The sidebar's Settings entry goes through the shell's slot callback rather
 * than navigating, so a second entry would mean changing that mechanism. Tabs
 * keep one route and one nav entry, and the three sections already had their
 * own card headings to become tab labels.
 */
const TABS = [
    { key: 'modules', label: 'Modules' },
    { key: 'general', label: 'General' },
    { key: 'billing', label: 'Corporate Billing' },
];

/**
 * Admin screen for the module switches.
 *
 * Deliberately NOT a general settings page. It lists exactly the catalogue the
 * API publishes (`GET /settings/features/admin`), so a new switch appears here
 * the moment it is added to `helpers/featureFlags.js` on the server and needs no
 * change on this screen. Anything outside that catalogue is rejected by the write
 * endpoint, so there is nothing to validate here either.
 *
 * Saves immediately on toggle rather than collecting an Update press: there is a
 * single boolean per row, no draft worth holding, and a half-applied set of
 * switches is a worse state than either end. Each row carries its own pending and
 * error state so a toggle is never silently ignored.
 */
const FeatureSettings = () => {
    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    // Keyed by feature key, so two rows can be in flight without fighting over
    // one shared "saving" boolean.
    const [saving, setSaving] = useState({});
    const [flash, setFlash] = useState('');

    // Valued settings (numbers), loaded and saved independently of the switches.
    const [settings, setSettings] = useState([]);
    // The text currently in each box, keyed by setting key. Held apart from the
    // saved value so a half-typed number is never mistaken for the real one.
    const [draft, setDraft] = useState({});
    const [savingSetting, setSavingSetting] = useState({});
    const [settingError, setSettingError] = useState({});

    const [tab, setTab] = useState('modules');

    const token = authStorage.getItem('auth-token');

    const reload = useCallback(() => axios({
        url: `${BASE_URL}/settings/features/admin`,
        method: 'GET',
        headers: { 'x-access-token': token },
    })
        .then((res) => setFeatures(res?.data?.data || []))
        .catch((err) => setError(err?.response?.data?.message || 'Could not load the settings.')),
    [token]);

    useEffect(() => {
        reload().finally(() => setLoading(false));
    }, [reload]);

    const reloadSettings = useCallback(() => axios({
        url: `${BASE_URL}/settings/general`,
        method: 'GET',
        headers: { 'x-access-token': token },
    })
        .then((res) => {
            const rows = res?.data?.data || [];
            setSettings(rows);
            // Seed the boxes from the server rather than from a default here, so
            // the screen can never show a different number from the one in force.
            const seeded = {};
            rows.forEach((s) => { seeded[s.key] = String(s.value); });
            setDraft(seeded);
        })
        .catch(() => { /* the switches above are the primary content; a settings
                          failure is reported per-row on save rather than taking
                          the whole screen down */ }),
    [token]);

    useEffect(() => { reloadSettings(); }, [reloadSettings]);

    const saveSetting = async (setting) => {
        const raw = draft[setting.key];
        const value = Number(raw);

        /*
         * Checked here as well as on the server. The server is the authority —
         * it rejects rather than clamps — but a round trip to be told "must be
         * between 1 and 90" for something the box already knows is a worse
         * experience than saying so immediately.
         */
        if (!String(raw).trim() || !Number.isFinite(value) || Math.round(value) !== value) {
            setSettingError((e) => ({ ...e, [setting.key]: 'Enter a whole number.' }));
            return;
        }
        if (value < setting.min || value > setting.max) {
            setSettingError((e) => ({
                ...e,
                [setting.key]: `Enter a number between ${setting.min} and ${setting.max}.`,
            }));
            return;
        }

        setSettingError((e) => ({ ...e, [setting.key]: '' }));
        setSavingSetting((s) => ({ ...s, [setting.key]: true }));
        try {
            const res = await axios({
                url: `${BASE_URL}/settings/general/${setting.key}`,
                method: 'PUT',
                headers: { 'x-access-token': token },
                data: { value },
            });
            setSettings((list) =>
                list.map((s) => (s.key === setting.key ? { ...s, value } : s))
            );
            setFlash(res?.data?.message || 'Saved');
            setTimeout(() => setFlash(''), 2500);
        } catch (err) {
            setSettingError((e) => ({
                ...e,
                [setting.key]: err?.response?.data?.message || 'Could not save that setting.',
            }));
        } finally {
            setSavingSetting((s) => ({ ...s, [setting.key]: false }));
        }
    };

    const toggle = async (feature) => {
        const next = !feature.enabled;
        setSaving((s) => ({ ...s, [feature.key]: true }));
        setError('');

        // Optimistic: the switch moves under the finger, and is put back below if
        // the write fails. Waiting for the round trip made it feel broken.
        setFeatures((list) =>
            list.map((f) => (f.key === feature.key ? { ...f, enabled: next } : f))
        );

        try {
            const res = await axios({
                url: `${BASE_URL}/settings/features/${feature.key}`,
                method: 'PUT',
                headers: { 'x-access-token': token },
                data: { enabled: next },
            });
            // Every client caches the flag map for the page's lifetime, so the
            // admin's own session has to be told to re-read it -- otherwise they
            // switch a module off and still see it in their own sidebar.
            refreshFeatureFlags();
            setFlash(res?.data?.message || 'Saved');
            setTimeout(() => setFlash(''), 2500);
            // Saving one switch can change what ANOTHER row means -- turning Full
            // Leaderboard off also suppresses My Performance. Patching this row
            // alone would leave the other showing a stale "Enabled".
            reload();
        } catch (err) {
            setFeatures((list) =>
                list.map((f) => (f.key === feature.key ? { ...f, enabled: !next } : f))
            );
            setError(err?.response?.data?.message || 'Could not save that setting.');
        } finally {
            setSaving((s) => ({ ...s, [feature.key]: false }));
        }
    };

    return (
        <div className="dark:bg-slate-900 p-6 min-h-screen">
            <h2 className="mob-header font-medium mb-5">Settings</h2>

            {/* Page level, not inside a card: a save in EITHER section confirms here.
                While this lived in the Modules card, saving a General setting flashed
                its confirmation up in an unrelated section. */}
            {flash && (
                <div className="mb-4 rounded border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800">
                    {flash}
                </div>
            )}

            {/* Plain buttons: this project loads only @tailwind utilities, with no
                preflight, so `border-0 appearance-none` has to be spelled out or
                Chrome's default button chrome shows through. */}
            <div className="mb-5 flex flex-wrap gap-1 border-0 border-b border-solid border-gray-200">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => setTab(t.key)}
                        className="border-0 appearance-none bg-transparent px-4 py-2 text-sm font-medium cursor-pointer"
                        style={
                            tab === t.key
                                ? { color: BRAND, boxShadow: `inset 0 -2px 0 0 ${BRAND}` }
                                : { color: '#6b7280' }
                        }
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === 'billing' && <CorporateBilling onFlash={(m) => {
                setFlash(m);
                setTimeout(() => setFlash(''), 3500);
            }} />}

            {tab === 'modules' && (
            <div className="bg-white rounded-lg p-5">
                <div className="mb-5">
                    <h3 className="text-base font-semibold text-gray-900">Modules</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        Turn a module off to hide it for everyone. The navigation entry disappears
                        and the pages behind it stop answering, so a member cannot reach one from a
                        saved link either. Nothing is deleted &mdash; switching it back on restores
                        it with all its history.
                    </p>
                </div>

                {error && (
                    <div className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
                        {error}
                    </div>
                )}

                {loading ? (
                    <Skeleton count={2} height={72} />
                ) : features.length === 0 ? (
                    <p className="py-10 text-center text-sm text-gray-500">
                        There are no switchable modules.
                    </p>
                ) : (
                    <div className="divide-y divide-gray-200 border-0 border-t border-solid border-gray-200">
                        {features.map((f) => {
                            const busy = Boolean(saving[f.key]);
                            // Its own switch is on, but a parent switch is off. Showing
                            // a plain "Disabled" here would be a lie about what the admin
                            // set, and would make the parent look broken.
                            const blocked = Boolean(f.blockedBy) && f.enabled;
                            return (
                                <div
                                    key={f.key}
                                    className="flex flex-wrap items-start justify-between gap-4 py-4"
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-gray-900">
                                                {f.label}
                                            </span>
                                            <span
                                                className="rounded-full px-2 py-0.5 text-[11px] font-medium"
                                                style={
                                                    blocked
                                                        ? { backgroundColor: '#fffbeb', color: '#b45309' }
                                                        : f.enabled
                                                            ? { backgroundColor: '#ecfdf5', color: '#047857' }
                                                            : { backgroundColor: '#f3f4f6', color: '#6b7280' }
                                                }
                                            >
                                                {blocked ? 'Off with parent' : f.enabled ? 'Enabled' : 'Disabled'}
                                            </span>
                                        </div>
                                        <p className="mt-1 text-sm text-gray-500">{f.description}</p>
                                        {blocked && (
                                            <p className="mt-1 text-sm font-medium" style={{ color: '#b45309' }}>
                                                Hidden because {f.blockedBy} is off. This switch is kept as you
                                                set it and takes effect again when {f.blockedBy} is turned back on.
                                            </p>
                                        )}
                                    </div>

                                    {/* NOTE: this project loads only `@tailwind utilities` (see
                                        src/index.css) -- there is NO preflight, so a bare <button>
                                        keeps Chrome's default outset border and button face. Hence
                                        `border-0 appearance-none` spelled out; drop them and this
                                        renders as a grey block, not a switch. */}
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={f.enabled}
                                        aria-label={`${f.label} ${f.enabled ? 'enabled' : 'disabled'}`}
                                        disabled={busy}
                                        onClick={() => toggle(f)}
                                        className={`relative h-6 w-11 shrink-0 rounded-full border-0 p-0 appearance-none transition-colors ${
                                            busy ? 'cursor-wait opacity-60' : 'cursor-pointer'
                                        }`}
                                        /* Blocked reads as a third state: the switch is still
                                           where the admin left it, but muted, so it does not
                                           claim to be doing anything right now. */
                                        style={{
                                            backgroundColor: blocked
                                                ? '#fcd34d'
                                                : f.enabled
                                                    ? BRAND
                                                    : '#d1d5db',
                                        }}
                                    >
                                        <span
                                            className="absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all"
                                            style={{ left: f.enabled ? '1.375rem' : '0.125rem' }}
                                        />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            )}

            {tab === 'general' && settings.length > 0 && (
                <div className="bg-white rounded-lg p-5">
                    <div className="mb-5">
                        <h3 className="text-base font-semibold text-gray-900">General</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            Values that apply across the platform. Each one takes effect as soon as
                            it is saved.
                        </p>
                    </div>

                    <div className="divide-y divide-gray-200 border-0 border-t border-solid border-gray-200">
                        {settings.map((s) => {
                            const busy = Boolean(savingSetting[s.key]);
                            const invalid = Boolean(settingError[s.key]);
                            const dirty = String(draft[s.key]) !== String(s.value);
                            return (
                                <div
                                    key={s.key}
                                    className="flex flex-wrap items-start justify-between gap-4 py-4"
                                >
                                    <div className="min-w-0 flex-1">
                                        <label
                                            htmlFor={`setting-${s.key}`}
                                            className="text-sm font-medium text-gray-900"
                                        >
                                            {s.label}
                                        </label>
                                        <p className="mt-1 text-sm text-gray-500">{s.description}</p>
                                        {invalid && (
                                            <p className="mt-1 text-sm font-medium" style={{ color: '#b91c1c' }}>
                                                {settingError[s.key]}
                                            </p>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-2">
                                        {/* The global stylesheet sets `input { border: ... !important }`,
                                            which beats even an inline style — so an invalid box is
                                            marked with a box-shadow ring rather than a red border. */}
                                        <input
                                            id={`setting-${s.key}`}
                                            type="number"
                                            min={s.min}
                                            max={s.max}
                                            value={draft[s.key] ?? ''}
                                            disabled={busy}
                                            onChange={(e) => {
                                                setDraft((d) => ({ ...d, [s.key]: e.target.value }));
                                                if (settingError[s.key]) {
                                                    setSettingError((x) => ({ ...x, [s.key]: '' }));
                                                }
                                            }}
                                            className="w-20 px-2 py-1 text-sm text-gray-900 text-center"
                                            style={invalid ? { boxShadow: '0 0 0 2px #fca5a5' } : undefined}
                                        />
                                        {s.unit && (
                                            <span className="text-sm text-gray-500">{s.unit}</span>
                                        )}
                                        <button
                                            type="button"
                                            disabled={busy || !dirty}
                                            onClick={() => saveSetting(s)}
                                            /* border-0 appearance-none spelled out: this project loads
                                               only @tailwind utilities, with no preflight, so a bare
                                               <button> otherwise keeps Chrome's default chrome. */
                                            className={`rounded-md border-0 appearance-none px-3 py-1.5 text-sm font-medium text-white ${
                                                busy || !dirty ? 'cursor-default opacity-50' : 'cursor-pointer'
                                            }`}
                                            style={{ backgroundColor: BRAND }}
                                        >
                                            {busy ? 'Saving…' : 'Save'}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FeatureSettings;
